<template>
  <div>
    <b-table
      id="endpoint-threats-table"
      class="table-with-actions-min-height"
      responsive
      small
      show-empty
      primary-key="id"
      :items="alerts"
      :fields="tableFields"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="filter"
      @filtered="onFiltered"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
    >
    <template #table-colgroup="scope">
      <col
        v-for="field in scope.fields"
        :key="field.key"
        :style="{ width: colWidth(field.key), minWidth: colMinWidth(field.key) }"
      >
    </template>
    <template #cell(hostname)="row">
      <div>
          <b-link :to="`/xtends/${row.item.id}`">{{ row.item.hostname }}</b-link>
          <span v-if="index < row.item.hostname.length - 1">, </span>
      </div>
    </template>
    <template #cell(threatInfo)="{ item, toggleDetails }">
      <b-row>
        <b-col>{{ item.events.join(', ') }}</b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button
            variant="flat-primary"
            class="p-0"
            @click="showRowDetail({ item, toggleDetails }, 'alerts')"
          >
            {{ item.count }} record(s)
          </b-button>
        </b-col>
      </b-row>
    </template>
    <template #cell(firstSeen)="row">
      {{ formatDate(row.value) }}
    </template>
    <template #cell(lastSeen)="row">
      {{ formatDate(row.value) }}
    </template>
    <template #cell(severity)="row">
      <b-badge pill :variant="alertStatusVariant(row.value)">
        {{ alertStatusText(row.value) }}
      </b-badge>
    </template>
    <template #cell(actions)="{ item, toggleDetails }">
      <div class="d-flex align-items-center">
        <b-icon
          icon="stars"
          class="cursor-pointer text-primary"
          title="AI Suggestions"
          @click="showRowDetail({ item, toggleDetails }, 'ai')"
        />
        <b-icon
          icon="box-arrow-up-right"
          class="cursor-pointer text-primary ml-2"
          title="ATT&CK Navigator"
          @click="openNavigator(item)"
        />
        <b-icon
          icon="eye"
          class="cursor-pointer text-primary ml-2"
          title="Show Evidence"
          @click="showRowDetail({ item, toggleDetails }, 'events')"
        />
      </div>
    </template>
    <template #row-details="{ item, toggleDetails }">
      <div>
        <ThreatAlertsTable
          v-if="detailMode[item.id] === 'alerts'"
          :alerts="item.alerts"
          @close="() => closeDetail({ item, toggleDetails })"
        />
        <EndpointThreatEventsTable
          v-if="detailMode[item.id] === 'events'"
          :alerts="item.alerts"
          @close="() => closeDetail({ item, toggleDetails })"
        />
        <AlertSuggestionsContent
          v-else-if="detailMode[item.id] === 'ai'"
          :alertAggkey="item.alerts[0].hostname"
          @close="() => closeDetail({ item, toggleDetails })"
        />
      </div>
    </template>
  </b-table>
  <b-pagination
    v-model="currentPage"
    :total-rows="paginationTotalRows"
    :per-page="perPage"
    first-number
    last-number
    prev-class="prev-item"
    next-class="next-item"
    align="right"
    class="mt-1 mr-1"
    ariaControls="endpoint-threats-table"
  ></b-pagination>
  </div>
</template>

<script>
import moment from 'moment';

import momentTimeFormat from '@/xvisor/constants/momentTimeFormat';
import colorPalette from '@/xvisor/constants/colorPalette';
import ThreatAlertsTable from '@/xvisor/components/security/ThreatAlertsTable.vue';
import EndpointThreatEventsTable from '@/xvisor/components/security/EndpointThreatEventsTable.vue';
import AlertSuggestionsContent from '@/xvisor/components/app/AlertSuggestionsContent.vue';
import alertSeverities from '@/xvisor/constants/alertSeverities';

export default {
  components: {
    ThreatAlertsTable,
    EndpointThreatEventsTable,
    AlertSuggestionsContent,
  },
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    filter: {
      type: String,
      default: '',
    },
    perPage: {
      type: Number,
      required: true,
    },
    items: {
      type: Array,
      default: null,
    },
    navigatorConfig: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      alerts: [],
      currentPage: 1,
      colorPalette,
      sortBy: 'severity',
      sortDesc: false,
      filteredLength: null,
      baseUrl: '',
      baseLayerUrl: '',
      detailMode: {},
      tableFields: [
        { key: 'hostname', label: 'Hostname', sortable: true },
        { key: 'threatInfo', label: 'Threat Info' },
        { key: 'firstSeen', label: 'First Seen', sortable: true },
        { key: 'lastSeen', label: 'Last Seen', sortable: true },
        { key: 'severity', label: 'Severity', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
    };
  },
  computed: {
    paginationTotalRows() {
      return this.filteredLength !== null ? this.filteredLength : this.alerts.length;
    },
  },
  watch: {
    timeRange() {
      this.httpGet();
      this.currentPage = 1;
    },
    filter() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    },
    items: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val)) {
          this.alerts = val;
          this.$emit('total-rows', this.alerts.length);
        }
      },
    },
  },
  mounted() {
    if (!this.items) this.httpGet();
  },
  methods: {
    alertStatusVariant(status) {
      if (status === alertSeverities.critical) return 'danger';
      if (status === alertSeverities.warning) return 'warning';
      return 'success';
    },
    alertStatusText(status) {
      if (status === alertSeverities.critical) return 'Critical';
      if (status === alertSeverities.warning) return 'Warning';
      return 'Notice';
    },
    httpGet() {
      this.$http
        .get('/alerts/endpoint/threats.json', {
          params: {
            start: this.timeRange.start.toISOString(),
            end: this.timeRange.end.toISOString(),
          },
        })
        .then((response) => {
          this.alerts = response.data;
          this.$emit('total-rows', Array.isArray(this.alerts) ? this.alerts.length : 0);
        });
    },
    onFiltered(filteredItems) {
      this.$emit('filtered-length', filteredItems.length);
      this.filteredLength = filteredItems.length;
      this.currentPage = 1;
    },
    formatDate(dateStr) {
      return moment(dateStr).format(momentTimeFormat.dateTime);
    },
    colWidth(fieldKey) {
      switch (fieldKey) {
        case 'hostname': return '15%';
        case 'threatInfo': return '50%';
        case 'firstSeen': return '10%';
        case 'lastSeen': return '10%';
        case 'severity': return '5%';
        case 'actions': return '10%';
        default: return '180px';
      }
    },
    colMinWidth(fieldKey) {
      switch (fieldKey) {
        case 'hostname': return '100px';
        case 'threatInfo': return '500px';
        case 'firstSeen': return '100px';
        case 'lastSeen': return '100px';
        case 'severity': return '50px';
        case 'actions': return '100px';
        default: return '80px';
      }
    },
    showRowDetail({ item, toggleDetails }, mode) {
      const isSameMode = this.detailMode[item.id] === mode;
      const isOpen = this.detailMode[item.id] !== undefined;

      if (isSameMode) {
        this.$delete(this.detailMode, item.id);
        toggleDetails();
      } else {
        this.$set(this.detailMode, item.id, mode);
        if (!isOpen) toggleDetails();
      }
    },
    closeDetail({ item, toggleDetails }) {
      this.$delete(this.detailMode, item.id);
      toggleDetails();
    },
    calculateNavigatorLink(item) {
      const start = this.timeRange.start.toISOString();
      const end = this.timeRange.end.toISOString();
      const cfg = this.navigatorConfig;
      const id = item?.hostname;
      const layerUrl = `${cfg.endpointLayerBase}${id}.json?start=${start}&end=${end}`;
      return `${cfg.baseUrl}${encodeURIComponent(layerUrl)}`;
    },
    openNavigator(item) {
      window.open(this.calculateNavigatorLink(item), '_blank');
    },
  },
};
</script>
